import SEOHead from '@/components/SEOHead';

const LAST_UPDATED = 'May 4, 2026';
const CONTACT_EMAIL = 'billing@builderslab.net';

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="text-xl font-bold text-white mb-4 pb-3 border-b border-zinc-800">{title}</h2>
      <div className="space-y-4 text-zinc-400 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-20">
      <SEOHead
        title="Refund Policy — BuildersLab"
        description="BuildersLab's refund policy for subscriptions, credits, and one-off purchases. Learn when and how to request a refund."
        ogUrl="https://builderslab.net/RefundPolicy"
        noIndex={false}
      />
      <div className="max-w-4xl mx-auto px-6">

        <div
         
         
         
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold uppercase tracking-wider mb-6">
            Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
            Refund Policy
          </h1>
          <p className="text-zinc-500 text-sm">Last updated: {LAST_UPDATED}</p>
          <p className="text-zinc-400 mt-4 text-base leading-relaxed max-w-2xl">
            We want you to be completely satisfied with BuildersLab. This policy explains when refunds
            are available, how to request one, and what to expect.
          </p>
        </div>

        <div
         
         
         
        >
          <Section title="1. 14-Day Money-Back Guarantee">
            <p>
              If you subscribe to a paid plan (Starter, Pro, or Agency) for the first time and are
              not satisfied, you may request a full refund within <strong className="text-white">14 calendar days</strong> of
              your initial payment. No questions asked.
            </p>
            <p>
              To claim this guarantee, email{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:underline">
                {CONTACT_EMAIL}
              </a>{' '}
              with the subject line <em>"Refund Request"</em> from the email address associated with
              your account.
            </p>
            <p>
              The 14-day guarantee applies to your <strong className="text-white">first</strong> subscription
              purchase only. It does not apply to plan upgrades, renewals, or repeat subscriptions
              after a previous refund has been issued.
            </p>
          </Section>

          <Section title="2. Monthly Subscriptions">
            <p>
              Monthly subscriptions are billed in advance for each billing period. Outside of the
              14-day money-back window:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Monthly subscription charges are <strong className="text-white">non-refundable</strong> once the billing period has begun.</li>
              <li>You may cancel at any time and your access will continue until the end of the current paid period.</li>
              <li>No partial-month refunds are issued.</li>
            </ul>
          </Section>

          <Section title="3. Annual Subscriptions">
            <p>
              Annual plans are billed upfront for 12 months at a discounted rate. If you cancel an
              annual plan after the 14-day refund window:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                You are eligible for a <strong className="text-white">pro-rated refund</strong> for the
                unused full months remaining on your plan, less any discount received relative to the
                monthly rate.
              </li>
              <li>
                Refunds are calculated as:{' '}
                <code className="bg-zinc-800 px-1.5 py-0.5 rounded text-[13px] text-zinc-300">
                  (unused full months / 12) × annual price paid
                </code>
              </li>
              <li>Pro-rated refunds for annual plans will be processed within 10 business days.</li>
            </ul>
          </Section>

          <Section title="4. Credits">
            <p>
              AI generation credits — whether included with a subscription, topped up separately, or
              awarded via referrals — are <strong className="text-white">non-refundable</strong> and
              have no cash value. Unused credits expire according to your plan's reset schedule and
              are not carried over or refunded on cancellation.
            </p>
          </Section>

          <Section title="5. Done-For-You & Custom Projects">
            <p>
              For bespoke development work arranged through our Done-For-You studio, refund terms are
              governed by the individual Statement of Work (SOW) agreed before work begins. In the
              absence of a specific clause, the following apply:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>A 50% deposit is non-refundable once work has commenced.</li>
              <li>The remaining balance is refundable if we fail to deliver the agreed deliverables within the agreed timeline, subject to written notice and a reasonable cure period of 7 business days.</li>
              <li>No refunds are issued after final delivery and sign-off.</li>
            </ul>
          </Section>

          <Section title="6. Billing Errors">
            <p>
              If you were charged incorrectly — for example, charged twice, charged after cancellation,
              or charged an amount different from the advertised price — we will issue a full refund
              of the erroneous charge promptly, regardless of the timeframe. Please contact{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:underline">
                {CONTACT_EMAIL}
              </a>{' '}
              with proof of the incorrect charge.
            </p>
          </Section>

          <Section title="7. Chargebacks">
            <p>
              We ask that you contact us before initiating a chargeback with your bank or payment
              provider. Chargebacks issued for charges that fall within this policy may result in the
              immediate suspension of your account. We cooperate fully with all payment processor
              dispute processes.
            </p>
          </Section>

          <Section title="8. How to Request a Refund">
            <p>To request a refund, please:</p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>
                Email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:underline">
                  {CONTACT_EMAIL}
                </a>{' '}
                from the address on your account.
              </li>
              <li>Use the subject line: <em>"Refund Request — [your plan name]"</em></li>
              <li>Include your account email and the date of the charge you are disputing.</li>
            </ol>
            <p>
              We aim to respond to all refund requests within <strong className="text-white">2 business days</strong>.
              Approved refunds are returned to the original payment method within 5–10 business days,
              depending on your bank or card issuer.
            </p>
          </Section>

          <Section title="9. Contact">
            <p>
              Questions about this policy:{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:underline">
                {CONTACT_EMAIL}
              </a>
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
